import { useState } from 'react';
import api, { formatError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Upload, CheckCircle2, Loader2 } from 'lucide-react';

const FIELDS = [
  ['full_name', 'Full Name (as per PAN)'], ['date_of_birth', 'Date of Birth', 'date'], ['phone', 'Phone'],
  ['pan_number', 'PAN Number'], ['aadhaar_number', 'Aadhaar Number'], ['address', 'Address'], ['city', 'City'],
  ['state', 'State'], ['pin_code', 'PIN Code'], ['bank_holder_name', 'Account Holder Name'], ['bank_name', 'Bank Name'],
  ['bank_account_number', 'Account Number'], ['bank_ifsc', 'IFSC Code'], ['upi_id', 'UPI ID (optional)'],
];
const DOCS = [['pan', 'PAN Card'], ['aadhaar', 'Aadhaar'], ['photo', 'Your Photo']];

function DocUpload({ type, label, done, onUploaded }) {
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData(); fd.append('doc_type', type); fd.append('file', file);
    try { await api.post('/sales/onboarding/kyc-doc', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); toast.success(`${label} uploaded`); onUploaded(type); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <label className={`flex flex-col items-center justify-center gap-1.5 h-28 rounded-lg border-2 border-dashed cursor-pointer transition-colors ${done ? 'border-emerald-300 bg-emerald-50' : 'border-brand-border hover:bg-brand-bg'}`} data-testid={`sales-kyc-upload-${type}`}>
      {busy ? <Loader2 size={20} className="animate-spin text-brand-primary" /> : done ? <CheckCircle2 size={20} className="text-emerald-600" /> : <Upload size={20} className="text-brand-primary" />}
      <span className="text-xs font-medium">{label}</span>
      <span className="text-[10px] text-brand-text-secondary">{done ? 'Uploaded · tap to replace' : 'JPG, PNG or PDF · max 5MB'}</span>
      <input type="file" accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden" onChange={upload} />
    </label>
  );
}

export function SalesKycForm({ kyc, onSubmitted }) {
  const [form, setForm] = useState(() => Object.fromEntries(FIELDS.map(([k]) => [k, kyc?.[k] || ''])));
  const [docs, setDocs] = useState(() => Object.keys(kyc?.documents || {}));
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try { await api.post('/sales/onboarding/kyc', form); toast.success('KYC submitted'); onSubmitted(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); } finally { setSaving(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-5" data-testid="sales-kyc-form">
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {FIELDS.map(([k, l, t]) => (
          <div key={k} className="space-y-1"><Label className="text-xs">{l}</Label>
            <Input type={t || 'text'} value={form[k]} required={k !== 'upi_id'} onChange={e => setForm({ ...form, [k]: e.target.value })} data-testid={`sales-kyc-${k}`} /></div>
        ))}
      </div>
      <div className="grid grid-cols-3 gap-3">
        {DOCS.map(([t, l]) => <DocUpload key={t} type={t} label={l} done={docs.includes(t)} onUploaded={x => setDocs(d => [...new Set([...d, x])])} />)}
      </div>
      <Button type="submit" disabled={saving || docs.length < 3} className="bg-brand-primary text-white" data-testid="sales-kyc-submit">
        {saving ? <Loader2 size={15} className="animate-spin" /> : 'Submit KYC'}
      </Button>
    </form>
  );
}
